import { useMutation } from '@tanstack/react-query';
import _ from 'lodash';
import { useCallback, useRef } from 'react';
import { DataItem } from '../../../@types/DataItem';
import { listProducts } from '../services';

export type UsePasteProductsProps = {
  /** Valores selecionados agora no filtro de produtos */
  selectedValues: string[];
  /** Itens (label/value) conhecidos dos selecionados, para o picker continuar exibindo o nome */
  selectedItems: DataItem[];
  onPaste: (values: string[], items: DataItem[]) => void;
};

/**
 * "Colar lista de códigos" como operação própria, fora da busca do picker: cada confirmação faz a sua
 * requisição com isModal (mesmo que o texto seja igual ao da colagem anterior ou ao da busca digitada),
 * não reaproveita a resposta da busca comum e não se perde quando o menu fecha e limpa a busca.
 * O que volta é somado ao que está selecionado no momento da resposta, e não a um cache antigo.
 */
export const usePasteProducts = ({ selectedValues, selectedItems, onPaste }: UsePasteProductsProps) => {
  const selectionRef = useRef({ selectedValues, selectedItems });
  selectionRef.current = { selectedValues, selectedItems };

  const onPasteRef = useRef(onPaste);
  onPasteRef.current = onPaste;

  const { mutate, isLoading } = useMutation({
    mutationFn: (query: string) => listProducts(query, { isModal: true }),
    onSuccess: (pasted: DataItem[]) => {
      const { selectedValues, selectedItems } = selectionRef.current;
      const values = _.uniq([...selectedValues, ...(pasted ?? []).map(({ value }) => value)]);
      const items = _.uniqBy([...selectedItems.filter(({ value }) => selectedValues.includes(value)), ...(pasted ?? [])], 'value');
      // duas colagens que respondem antes do próximo render somam uma na outra
      selectionRef.current = { selectedValues: values, selectedItems: items };
      onPasteRef.current(values, items);
    },
  });

  const handlePaste = useCallback(
    (query: string) => {
      if (!query.trim().length) return;
      mutate(query);
    },
    [mutate],
  );

  return { handlePaste, isLoading } as const;
};
